import { isEqual, throttle, times } from 'lodash'
import { Node as ProseNode, ResolvedPos } from 'prosemirror-model'
import { EditorState, Plugin, PluginKey } from 'prosemirror-state'
import { Decoration, DecorationSet, EditorView } from 'prosemirror-view'

export const registeredActions = new Map<symbol, contextualActionDescriptor>()

export type contextualActionDescriptor = {
  active: (context: contextualActionContext) => boolean
  containerDeco: (
    hoverContext: contextualActionContext | null,
    caretContext: contextualActionContext | null,
  ) => Decoration[]
  getPortals: (
    view: EditorView,
    contextualPluginState: ContextualBlockActionsPluginState,
  ) => Array<{ element: React.ReactNode; container: Element; key: string }>
}

export const registerContextualAction = (name: symbol, contextualAction: contextualActionDescriptor) => {
  registeredActions.set(name, contextualAction)
}

export const ContextualBlockActionsPluginKey = new PluginKey('contextualBlockActions')

export type ContextualBlockActionsPluginState = {
  hoveredContext: null | contextualActionContext
  caretContext: null | contextualActionContext
}

export type contextualActionContext = Array<[node: ProseNode, pos: number]>

type ContextualBlockActionsPluginMeta = {
  action: 'setHovered'
  hoveredContext: null | contextualActionContext
}

const getContext = ($pos: ResolvedPos) => {
  const nodeAncestors: contextualActionContext = times($pos.depth, (i) => [
    $pos.node(i > 0 ? -i : undefined),
    $pos.before(i > 0 ? -i : undefined),
  ])
  return nodeAncestors
}

const shouldShowRightActions = (context: contextualActionContext) =>
  Array.from(registeredActions.values()).some((contextualAction) => contextualAction.active(context))

const handleMouseMove = throttle(
  (view: EditorView, event: Event) => {
    if (!(view as any).docView) return
    const {
      state: {
        tr,
        tr: { doc },
      },
      dispatch,
    } = view
    let target = event.target
    // if you ever want to deal with images, maybe the following snippet is helpful:
    // if (event.target instanceof Element) {
    //   const maybeImageElement = event.target.closest('[data-node-type="image"]')
    //   if (event.target.closest('.ProseMirror-widget')) {
    //     return
    //   }
    //   if (maybeImageElement) {
    //     target = maybeImageElement.querySelector('img')
    //   }
    // }
    const targetPosAtDom = view?.posAtDOM(target as Node, 0)
    if (targetPosAtDom === -1 || targetPosAtDom === undefined) return
    let $hoveredNodePos = doc.resolve(targetPosAtDom)
    const ctx = getContext($hoveredNodePos)

    if (shouldShowRightActions(ctx)) {
      let meta: ContextualBlockActionsPluginMeta = { action: 'setHovered', hoveredContext: ctx }
      dispatch(tr.setMeta(ContextualBlockActionsPluginKey, meta))
    }
  },
  100,
  { trailing: true },
)

export let ContextualBlockActionsPlugin = new Plugin<ContextualBlockActionsPluginState, any>({
  key: ContextualBlockActionsPluginKey,

  state: {
    init() {
      return { hoveredContext: null, caretContext: null }
    },
    apply(tr, pluginState, oldState, newState) {
      const meta: ContextualBlockActionsPluginMeta = tr.getMeta(ContextualBlockActionsPlugin)

      const { $from } = tr.selection
      let previousHoveredContext = pluginState.hoveredContext
      let caretContext: contextualActionContext | null = getContext($from)

      if (!shouldShowRightActions(caretContext)) {
        caretContext = null
      }
      if (!meta) {
        return {
          caretContext, // we don't map caretNodePos since it was just generated above
          hoveredContext:
            previousHoveredContext === null
              ? null
              : previousHoveredContext.map(([node, pos]) => [node, tr.mapping.map(pos)]),
        }
      } else {
        switch (meta.action) {
          case 'setHovered':
            return { caretContext, hoveredContext: meta.hoveredContext }
        }
      }
    },
  },
  props: {
    decorations: (state: EditorState) => {
      const { caretContext, hoveredContext } = ContextualBlockActionsPluginKey.getState(
        state,
      ) as ContextualBlockActionsPluginState
      const decorations = Array.from(registeredActions.values())
        .filter(
          (contextualAction) =>
            (caretContext && contextualAction.active(caretContext)) ||
            (hoveredContext && contextualAction.active(hoveredContext)),
        )
        .map(({ containerDeco }) => containerDeco(caretContext, hoveredContext))
        .flat()

      return decorations.length ? DecorationSet.create(state.doc, decorations) : DecorationSet.empty
    },
    handleDOMEvents: {
      mousemove: (view, event) => {
        handleMouseMove(view, event)
        return false
      },
      mouseout: (view, event) => {
        const {
          state,
          state: { doc },
        } = view
        const hoveredContext =
          (ContextualBlockActionsPluginKey.getState(state) as ContextualBlockActionsPluginState)?.hoveredContext ?? null

        // clear hoveredNodePos when the mouse leaves the editor area (if not dragging)
        if (event.relatedTarget instanceof Node && !view.dom.contains(event.relatedTarget) && hoveredContext !== null) {
          const meta: ContextualBlockActionsPluginMeta = { action: 'setHovered', hoveredContext: null }
          view.dispatch(view.state.tr.setMeta(ContextualBlockActionsPluginKey, meta))
          return false
        }
        // hovering into a node
        if (event.relatedTarget instanceof Node) {
          let meta: ContextualBlockActionsPluginMeta = { action: 'setHovered', hoveredContext: null }
          const targetPosAtDom = view?.posAtDOM(event.relatedTarget, 0)
          // target node is not in the editor, clear hoveredContext
          if (targetPosAtDom === -1 || targetPosAtDom === undefined) {
            view.dispatch(view.state.tr.setMeta(ContextualBlockActionsPluginKey, meta))
            return false
          }
          let $hoveredNodePos = doc.resolve(targetPosAtDom)
          const ctx = getContext($hoveredNodePos)
          if (isEqual(ctx, hoveredContext)) return false // bail early if hovering over the same node
          // new context is different, requires an update
          meta.hoveredContext = ctx
          view.dispatch(view.state.tr.setMeta(ContextualBlockActionsPluginKey, meta))
          return false
        }
        return false
      },
    },
  },
})
